import {AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {fromEvent, merge, Observable, Subject, Subscription} from 'rxjs';
import {takeUntil, throttleTime} from 'rxjs/operators';

@Directive({
    selector: '[appScrollIntoView]'
})
export class ScrollIntoViewDirective implements AfterViewInit, OnDestroy {
    @Input('appScrollIntoView') appScrollIntoView: boolean = true;
    @Input() classTarget: string = 'mat-mdc-card-header';
    @Output() scrollFinished: EventEmitter<void> = new EventEmitter<void>();

    private scrollEnd$: Subject<void> = new Subject<void>();
    private scrollSubscription!: Subscription;
    private observer!: MutationObserver;
    private isContentLoaded: boolean = false;
    private parentElement: any;
    private lastScrollPosition: number = 0;

    constructor(private readonly elRef: ElementRef) {
    }

    ngAfterViewInit(): void {
        if (this.appScrollIntoView) {
            this.initParentEl();

            this.observer = new MutationObserver((): void => this.checkContentDimension());
            this.observer.observe(this.parentElement, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['width', 'height']
            });

            setTimeout((): void => {
                this.checkContentDimension();
            }, 200);
        }
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
        this.scrollSubscription?.unsubscribe();
        this.scrollEnd$.next();
        this.scrollEnd$.complete();
    }

    private initParentEl(): void {
        this.parentElement = this.elRef.nativeElement.parentElement;

        while (this.parentElement && this.parentElement !== document.body && !this.isScrollable(this.parentElement)) {
            this.parentElement = this.parentElement.parentElement;
        }

        if (!this.parentElement || this.parentElement === document.body) {
            this.parentElement = document.body;
        }
    }

    private checkContentDimension(): void {
        const contentElements: HTMLElement[] = this.parentElement.querySelectorAll('img, video, iframe');
        const allLoaded: boolean = Array.from(contentElements).every(
            (el: HTMLElement): number => el.clientHeight && el.clientWidth
        );

        if (allLoaded && !this.isContentLoaded) {
            this.isContentLoaded = true;
            this.initializeScrollDetection();

            setTimeout((): void => {
                if (!this.isElementInViewport(this.getTargetElement())) {
                    this.scrollIntoViewIfNeeded();
                }
            }, 200);
        }
    }

    private initializeScrollDetection(): void {
        const scrollEvent$: Observable<unknown> = fromEvent(this.parentElement, 'scroll');
        const windowScrollEvent$: Observable<Event> = fromEvent(window, 'scroll');

        this.scrollSubscription = merge(scrollEvent$, windowScrollEvent$).pipe(
            throttleTime(100),
            takeUntil(this.scrollEnd$)
        ).subscribe((): void => {
            const currentScrollPosition: number = Math.max(window.scrollY, this.parentElement.scrollTop);
            const scrollingUp: boolean = currentScrollPosition < this.lastScrollPosition;
            this.lastScrollPosition = currentScrollPosition;

            if (scrollingUp) {
                this.scrollFinished.emit();
                window.history.replaceState(null, '', window.location.pathname);
                this.scrollEnd$.next();
                return;
            }

            const target: HTMLElement = this.getTargetElement();
            if (!this.isElementInViewport(target)) {
                this.scrollIntoViewIfNeeded();
            } else {
                this.scrollFinished.emit();
                window.history.replaceState(null, '', window.location.pathname);
                this.scrollEnd$.next();
            }
        });
    }

    private scrollIntoViewIfNeeded(): void {
        const target: HTMLElement = this.getTargetElement();
        if (!target) return;

        const header: Element = document.querySelector('.main-header')!;
        const titlePosition: number = target.getBoundingClientRect().top + window.scrollY;

        if (header && this.classTarget !== 'commentTarget') {
            const headerHeight: number = header.getBoundingClientRect().height;
            window.scrollTo({
                top: titlePosition - headerHeight,
                behavior: 'smooth'
            });
        } else {
            target.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    }

    private getTargetElement(): HTMLElement {
        return this.elRef.nativeElement.getElementsByClassName(this.classTarget)[0];
    }

    private isElementInViewport(el: HTMLElement): boolean {
        if (!el) return false;
        const rect: DOMRect = el.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    private isScrollable(element: HTMLElement): boolean {
        const style: CSSStyleDeclaration = window.getComputedStyle(element);
        return ['auto', 'scroll'].includes(style.overflowY);
    }
}
